import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAnalytics } from '../services/analytics';
import { LOGOUT } from '../services/analytics/event-names';
import { AccessibeUser } from '../type/AccessibeUser';
import { CmsContext } from '../type/CmsContext';
import { MerchantDetails } from '../type/MerchantDetails';
import { UserFeedback } from '../type/UserFeedback';
import { MESSAGE_EVENT, sendMessageToWrapper } from '../utils';
import { markUserLoggedOut } from '../utils/auth-utils';
import axios from '../utils/axiosInstance';
import { parseJwt } from '../utils/jwt-utils';
import { Logger } from '../utils/logger';

interface MerchantDetailsProviderProps {
  children: ReactNode;
}

const MerchantDetailsContext = createContext<
  | {
      merchantDetails: MerchantDetails | null;
      setMerchantDetails: React.Dispatch<
        React.SetStateAction<MerchantDetails | null>
      >;
      cmsContextToken?: string;
      setCmsContextToken: (context: string) => void;
      cmsContext: CmsContext | null;
      redirectUrl: string | null;
      setRedirectUrl: React.Dispatch<React.SetStateAction<string | null>>;
      accessibeUser: AccessibeUser | null;
      setAccessibeUser: (user: AccessibeUser | null) => void;
      onLogout: () => Promise<void>;
      willShowTrialStartedPopup: boolean;
      setWillShowTrialStartedPopup: React.Dispatch<
        React.SetStateAction<boolean>
      >;
      portalMixpanelProps: object;
      setPortalMixpanelProps: React.Dispatch<React.SetStateAction<object>>;
      toggleScript: (widgetStatus: boolean) => void;
      feedbackVisible: boolean;
      setFeedbackVisible: React.Dispatch<React.SetStateAction<boolean>>;
      userFeedback: UserFeedback | null;
      setUserFeedback: React.Dispatch<
        React.SetStateAction<UserFeedback | null>
      >;
      noEmailSignup: boolean;
      setNoEmailSignup: React.Dispatch<React.SetStateAction<boolean>>;
      noEmailSignupFallback: boolean;
      setNoEmailSignupFallback: React.Dispatch<React.SetStateAction<boolean>>;
      noEmailSignupProcessing: boolean;
      setNoEmailSignupProcessing: React.Dispatch<React.SetStateAction<boolean>>;
      showCalendlyAfterAutoSignup: boolean;
      setShowCalendlyAfterAutoSignup: React.Dispatch<
        React.SetStateAction<boolean>
      >;
    }
  | undefined
>(undefined);

const MerchantSessionProvider: React.FC<MerchantDetailsProviderProps> = ({
  children,
}) => {
  const analytics = useAnalytics();

  const [merchantDetails, _setMerchantDetails] =
    useState<MerchantDetails | null>(null);

  // Wrapper function to ensure widgetStatus is always a boolean
  const setMerchantDetails = useCallback(
    (
      value: // eslint-disable-next-line @typescript-eslint/no-explicit-any
      | (MerchantDetails & { widgetStatus?: any })
        | null
        | ((
            prev: MerchantDetails | null,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ) => (MerchantDetails & { widgetStatus?: any }) | null),
    ) => {
      _setMerchantDetails((prev) => {
        const newDetails = typeof value === 'function' ? value(prev) : value;
        if (
          newDetails &&
          'widgetStatus' in newDetails &&
          newDetails.widgetStatus !== undefined
        ) {
          const originalValue = newDetails.widgetStatus;
          let convertedValue: boolean;

          if (typeof originalValue === 'string') {
            if (
              originalValue === '1' ||
              originalValue.toLowerCase() === 'true'
            ) {
              convertedValue = true;
            } else {
              convertedValue = false;
            }
          } else {
            convertedValue = Boolean(originalValue);
          }

          if (typeof originalValue !== 'boolean') {
            Logger.log(
              `Converting widgetStatus from ${typeof originalValue}(${originalValue}) to boolean(${convertedValue})`,
            );
          }

          return {
            ...newDetails,
            widgetStatus: convertedValue,
          };
        }
        return newDetails;
      });
    },
    [],
  );
  const [accessibeUser, _setAccessibeUser] = useState<AccessibeUser | null>(
    null,
  );
  const [cmsContextToken, _setCmsContextToken] = useState<string>();
  const [cmsContext, _setCmsContext] = useState<CmsContext | null>(null);
  const [willShowTrialStartedPopup, setWillShowTrialStartedPopup] =
    useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(
    window.localStorage.getItem('redirectUrl'),
  );
  const [portalMixpanelProps, setPortalMixpanelProps] = useState<object>({});

  const [userFeedback, setUserFeedback] = useState<UserFeedback | null>(null);
  const [feedbackVisible, setFeedbackVisible] = useState<boolean>(false);

  const [noEmailSignup, setNoEmailSignup] = useState<boolean>(false);

  const [noEmailSignupFallback, setNoEmailSignupFallback] =
    useState<boolean>(false);

  const [noEmailSignupProcessing, setNoEmailSignupProcessing] =
    useState<boolean>(false);

  const [showCalendlyAfterAutoSignup, setShowCalendlyAfterAutoSignup] =
    useState<boolean>(false);

  const setCmsContextToken = useCallback((context: string) => {
    _setCmsContextToken(context);
    window.localStorage.setItem('cmsContext', context);
    try {
      const parsedContext = parseJwt(context);
      _setCmsContext(parsedContext);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      Logger.error('Error parsing cms context token', error);
    }
  }, []);

  const setAccessibeUser = useCallback(
    (user: AccessibeUser | null) => {
      Logger.log('Setting accessibe user', user);
      _setAccessibeUser(user);
      window.localStorage.setItem('accessibeUser', JSON.stringify(user));
      if (user) {
        let token: string | null = null;
        setNoEmailSignupFallback(false);
        axios
          .post('/api/signed-details/encode', { userId: user.userId })
          .then((response) => {
            token = response.headers['x-user-token'];
          })
          .finally(() => {
            sendMessageToWrapper(MESSAGE_EVENT.ACSB_USER_LOADED, {
              acsbUser: user,
              signedDetails: token,
            });
          });
      }
      if (user && window.identifyFSUser) {
        window.identifyFSUser(
          user.userId,
          user.email,
          user.fullName,
          merchantDetails?.source ?? 'unknown',
        );
        Logger.log('Identified user in FullStory');
      }
    },
    [merchantDetails?.source],
  );

  const onLogout = useCallback(async () => {
    await axios.get('/api/accessibe/logout', {
      withCredentials: true,
    });
    markUserLoggedOut();
    setAccessibeUser(null);
    setMerchantDetails((prev) => {
      if (prev) {
        return { ...prev, licenseId: '' };
      } else {
        return {
          source: '',
          storeId: '',
          licenseId: '',
          widgetStatus: false,
        };
      }
    });
    Logger.log('logging out');
    sendMessageToWrapper(MESSAGE_EVENT.LOGOUT);
    analytics.mixpanel.trackEvent(LOGOUT, {
      pluginSource: merchantDetails?.source,
    });

    analytics.mixpanel.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, merchantDetails, setAccessibeUser]);

  useEffect(() => {
    if (!cmsContext || !cmsContextToken) {
      const context =
        new URL(window.location.href).searchParams.get('context') ||
        window.localStorage.getItem('cmsContext');
      if (context) {
        setCmsContextToken(context);
      }
    }
  }, [cmsContext, cmsContextToken, setCmsContextToken]);

  useEffect(() => {
    if (!accessibeUser) {
      const user = window.localStorage.getItem('accessibeUser');
      if (user) {
        setAccessibeUser(JSON.parse(user));
      }
    }
  }, [accessibeUser, setAccessibeUser]);

  const toggleScript = (widgetStatus: boolean) => {
    if (widgetStatus) {
      sendMessageToWrapper(MESSAGE_EVENT.ADD_SCRIPT, { widgetStatus: true });
    } else {
      sendMessageToWrapper(MESSAGE_EVENT.REMOVE_SCRIPT, {
        widgetStatus: false,
      });
    }
    setMerchantDetails((prev) => {
      if (prev) {
        return { ...prev, widgetStatus };
      }
      return prev;
    });
  };

  return (
    <MerchantDetailsContext.Provider
      value={{
        merchantDetails,
        setMerchantDetails,
        cmsContextToken,
        setCmsContextToken,
        cmsContext,
        redirectUrl,
        setRedirectUrl,
        accessibeUser,
        setAccessibeUser,
        onLogout,
        willShowTrialStartedPopup,
        setWillShowTrialStartedPopup,
        portalMixpanelProps,
        setPortalMixpanelProps,
        toggleScript,
        userFeedback,
        setUserFeedback,
        feedbackVisible,
        setFeedbackVisible,
        noEmailSignup,
        setNoEmailSignup,
        noEmailSignupFallback,
        setNoEmailSignupFallback,
        noEmailSignupProcessing,
        setNoEmailSignupProcessing,
        showCalendlyAfterAutoSignup,
        setShowCalendlyAfterAutoSignup,
      }}
    >
      {children}
    </MerchantDetailsContext.Provider>
  );
};

const useMerchantSession = () => {
  const context = useContext(MerchantDetailsContext);
  if (context === undefined) {
    throw new Error(
      'useMerchantSession must be used within a MerchantDetailsProvider',
    );
  }
  return context;
};

export { MerchantSessionProvider, useMerchantSession };
